import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatConversationTime,
  humanizeIdentifier,
  inferConversationLanguage,
  normalizeReportConversationMessages,
  splitAssistantMessageParts,
} from '../utils/reportConversationDisplay';
import {
  formatSourceLabel,
  formatVitalSignLabel,
} from '../utils/vitalSignDisplay';
import { getLocaleDirection } from '../utils/locale';

const avatarIcon = (role) => {
  if (role === 'user') {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="8" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="11" width="18" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="5" r="2" stroke="currentColor" strokeWidth="2" />
      <path d="M12 7v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="9" cy="16" r="1" fill="currentColor" />
      <circle cx="15" cy="16" r="1" fill="currentColor" />
    </svg>
  );
};

const systemIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M9 9h6v6H9z" fill="currentColor" />
    <path d="M9 1v4M15 1v4M9 19v4M15 19v4M1 9h4M1 15h4M19 9h4M19 15h4" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const formatExamType = (type, t, conversationLanguage) => {
  const key = String(type || '').trim();
  if (!key) {
    return '';
  }
  return t(`checkTypes.${key}`, {
    defaultValue: formatVitalSignLabel(key, conversationLanguage),
  });
};

const getRecordingLabel = (tagName, t) => {
  const labelMap = {
    subjective: t('reports.subjectiveSymptom'),
    objective: t('reports.objectiveExam'),
    assessment: t('reports.assessment'),
    plan: t('reports.plan'),
    diagnosis: t('reports.diagnosis'),
    treatment: t('reports.treatment'),
  };
  return labelMap[tagName] || humanizeIdentifier(tagName);
};

const ToolResultCard = ({ result, t, conversationLanguage }) => {
  const rows = [
    result.type ? { label: t('reports.checkType'), value: formatExamType(result.type, t, conversationLanguage) } : null,
    result.value ? { label: t('reports.checkResult'), value: [result.value, result.unit].filter(Boolean).join(' ') } : null,
    result.source ? { label: t('reports.source'), value: formatSourceLabel(result.source, conversationLanguage) } : null,
    result.executed_at ? { label: t('reports.executedAt'), value: formatConversationTime(result.executed_at, conversationLanguage) || result.executed_at } : null,
  ].filter(Boolean);

  return (
    <div className="conversation-tool-result">
      <div className="conversation-tool-result__summary">
        {rows.map((row) => (
          <div className="conversation-tool-result__item" key={row.label}>
            <span className="conversation-tool-result__label">{row.label}</span>
            <bdi className="conversation-tool-result__value" dir="auto">{row.value}</bdi>
          </div>
        ))}
      </div>

      {result.details?.length > 0 && (
        <table className="conversation-tool-result__details">
          <tbody>
            {result.details.map((detail) => (
              <tr key={detail.key}>
                <th>{t(`checkTypes.${detail.key}`, { defaultValue: formatVitalSignLabel(detail.key, conversationLanguage, detail) })}</th>
                <td dir="ltr">{[detail.value, detail.unit].filter(Boolean).join(' ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const AssistantContent = ({ message, t, conversationLanguage }) => {
  const parts = splitAssistantMessageParts(message.content);
  const visibleParts = parts.filter((part) => part.type !== 'tool_call');

  return (
    <>
      {visibleParts.length > 0 && (
        <div className="conversation-message conversation-message--assistant">
          <div className="conversation-avatar conversation-avatar--assistant">{avatarIcon('assistant')}</div>
          <div className="conversation-message__body">
            <div className="conversation-message__meta">
              {t('reports.assistantMessage')} | {formatConversationTime(message.created_at, conversationLanguage)}
            </div>
            <div
              className="conversation-bubble conversation-bubble--assistant"
              lang={conversationLanguage}
              dir={getLocaleDirection(conversationLanguage)}
            >
              {visibleParts.map((part, partIndex) => (
                part.type === 'recording' ? (
                  <div className={`conversation-recording conversation-recording--${part.tagName}`} key={`${part.type}-${partIndex}`}>
                    <strong>{getRecordingLabel(part.tagName, t)}</strong>
                    <span>{part.text}</span>
                  </div>
                ) : (
                  <p className="conversation-text-block" key={`${part.type}-${partIndex}`}>{part.text}</p>
                )
              ))}
            </div>
          </div>
        </div>
      )}

    </>
  );
};

const ParticipantMessage = ({ message, t, conversationLanguage }) => {
  const isUser = message.role === 'user';

  return (
    <div className={`conversation-message ${isUser ? 'conversation-message--user' : 'conversation-message--assistant'}`}>
      <div className={`conversation-avatar ${isUser ? 'conversation-avatar--user' : 'conversation-avatar--assistant'}`}>
        {avatarIcon(isUser ? 'user' : 'assistant')}
      </div>
      <div className="conversation-message__body">
        <div className="conversation-message__meta">
          <span style={{ fontWeight: 500 }}>{isUser ? t('reports.userMessage') : t('reports.assistantMessage')}</span>
          <span>|</span>
          <span>{formatConversationTime(message.created_at, conversationLanguage)}</span>
        </div>
        <div
          className={`conversation-bubble ${isUser ? 'conversation-bubble--user' : 'conversation-bubble--assistant'}`}
          lang={conversationLanguage}
          dir={getLocaleDirection(conversationLanguage)}
        >
          <p className="conversation-text-block">{message.content}</p>
        </div>
      </div>
    </div>
  );
};

const SystemMessage = ({ message, t, conversationLanguage }) => (
  <div className="conversation-message conversation-message--system">
    <div className="conversation-message__body conversation-message__body--system">
      <div className="conversation-message__meta">
        {t('reports.systemMessage')} | {formatConversationTime(message.created_at, conversationLanguage)}
      </div>
      <div className="conversation-bubble conversation-bubble--system">
        <span className="conversation-system-icon">{systemIcon}</span>
        <div className="conversation-system-content">
          {message.toolResults?.length > 0 ? (
            message.toolResults.map((result, index) => (
              <ToolResultCard result={result} t={t} conversationLanguage={conversationLanguage} key={`${result.type || 'tool'}-${index}`} />
            ))
          ) : (
            <p className="conversation-text-block">{message.fallbackText || message.content}</p>
          )}
        </div>
      </div>
    </div>
  </div>
);

const ConversationMessage = ({ message, t, conversationLanguage }) => {
  if (message.role === 'system') {
    return <SystemMessage message={message} t={t} conversationLanguage={conversationLanguage} />;
  }
  if (message.role === 'assistant') {
    return <AssistantContent message={message} t={t} conversationLanguage={conversationLanguage} />;
  }
  return <ParticipantMessage message={message} t={t} conversationLanguage={conversationLanguage} />;
};

const ReportConversationHistory = ({
  messages,
  loading,
  error,
  show,
  onToggle,
  visitType,
  conversationLanguage,
  title,
  emptyText,
  loadingText,
  showText,
  hideText,
  toggleButtonStyle,
  onToggleButtonMouseEnter,
  onToggleButtonMouseLeave,
}) => {
  const { t, i18n } = useTranslation();
  const conversationDisplayLanguage = useMemo(
    () => inferConversationLanguage(messages, conversationLanguage),
    [messages, conversationLanguage]
  );
  const conversationT = useMemo(
    () => i18n.getFixedT(conversationDisplayLanguage || 'en-US'),
    [i18n, conversationDisplayLanguage]
  );
  const displayMessages = useMemo(
    () => normalizeReportConversationMessages(messages, { visitType, conversationLanguage }),
    [messages, visitType, conversationLanguage]
  );

  const sectionTitle = title || t('reports.conversationHistory');
  const emptyMessage = emptyText || t('reports.noConversationData');
  const loadingMessage = loadingText || t('reports.loadingConversation');

  return (
    <>
      <div className="report-conversation-header report-section-title-row">
        <h3 className="report-conversation-title">{sectionTitle}</h3>
        <button
          type="button"
          onClick={onToggle}
          style={toggleButtonStyle}
          onMouseEnter={onToggleButtonMouseEnter}
          onMouseLeave={onToggleButtonMouseLeave}
        >
          {show ? (hideText || t('reports.hideConversation')) : (showText || t('reports.showConversation'))}
        </button>
      </div>

      {show && (
        <div>
          {loading ? (
            <div className="report-conversation-state">
              <div className="loading-spinner" aria-hidden="true"></div>
              <p>{loadingMessage}</p>
            </div>
          ) : error ? (
            <div className="report-conversation-error">{error}</div>
          ) : displayMessages.length === 0 ? (
            <p className="report-conversation-empty">{emptyMessage}</p>
          ) : (
            <div
              className="report-conversation-list"
              lang={conversationDisplayLanguage}
              dir={getLocaleDirection(conversationDisplayLanguage)}
            >
              {displayMessages.map((message, index) => (
                <ConversationMessage
                  message={message}
                  t={conversationT}
                  conversationLanguage={conversationDisplayLanguage}
                  key={message.id || `${message.role}-${message.created_at || index}-${index}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ReportConversationHistory;
